import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import CpiRadar from "@/components/site/CpiRadar";
import HudCorners from "@/components/site/_partials/HudCorners";
import { FadeUp, HRule } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, breadcrumbSchema, SITE_URL, OG_IMAGES } from "@/lib/seo";
import { ACTIVATION_URL } from "@/lib/links";
import { PostIntakeSteps } from "@/components/site/_partials/PostIntakeSteps";
import { CPI_DOMAINS, TIERS, SAMPLE_PROFILE } from "@/data/cpi";

const byCode = Object.fromEntries(CPI_DOMAINS.map((d) => [d.code, d]));

function Panel({ label, children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`border-2 border-primary/15 bg-card p-6 md:p-8 ${className}`}>
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/60 mb-4">
        {label}
      </div>
      {children}
    </div>
  );
}

function FindingsTable({ rows }) {
  return (
    <dl className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-12 gap-2 items-baseline border-b border-border pb-2 last:border-0">
          <dt className="col-span-5 text-sm text-foreground/65">{r.label}</dt>
          <dd className="col-span-3 font-mono text-sm font-bold text-primary">{r.value}</dd>
          <dd className="col-span-4 text-right font-mono text-[0.6rem] uppercase tracking-[0.1em] text-accent">
            {r.note}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function SampleAnalysis() {
  useDocumentMeta({
    title: "Sample Optimization Analysis | Integrated Wellness",
    description:
      "A fictional, de-identified example of an Integrated Wellness optimization analysis — CPI profile across 10 domains, optimization archetype, findings, priorities and the Tier 1–4 framework.",
    image: OG_IMAGES.sampleAnalysis,
    url: `${SITE_URL}/sample-analysis`,
    schema: [
      organizationSchema(),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Sample Optimization Analysis", url: "/sample-analysis" },
      ]),
    ],
  });

  return (
    <div data-testid="sample-analysis-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-36 pb-20 md:pb-28">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <FadeUp>
            <div className="iw-overline">Sample Optimization Analysis</div>
            <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(2rem,4.8vw,3.5rem)] text-primary">
              From Data to an <em className="not-italic text-accent">Actionable Optimization Plan</em>
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span
                data-testid="sample-fictional-badge"
                className="font-mono text-[0.6rem] uppercase tracking-[0.18em] font-bold text-accent border border-accent/40 px-3 py-1.5"
              >
                Fictional · De-Identified Example
              </span>
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-foreground/50 border border-border px-3 py-1.5">
                {SAMPLE_PROFILE.id} · {SAMPLE_PROFILE.meta}
              </span>
            </div>
          </FadeUp>

          {/* CPI Profile */}
          <FadeUp delay={0.08} className="mt-12">
            <div className="relative border-2 border-primary/15 bg-card p-4 md:p-8">
              <HudCorners offset={10} size={12} />
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-6">
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/60 mb-2">
                    Clinical Performance Index · 10 Domains
                  </div>
                  <CpiRadar domains={CPI_DOMAINS} className="w-full max-w-[440px] mx-auto" />
                </div>
                <div className="lg:col-span-6 space-y-3">
                  {CPI_DOMAINS.map((d) => (
                    <div key={d.code} className="flex items-center gap-3">
                      <span className="font-mono text-[0.58rem] tracking-[0.14em] font-bold text-accent w-9">
                        {d.code}
                      </span>
                      <span className="text-sm text-foreground/75 w-44 md:w-52 truncate">{d.name}</span>
                      <div className="flex-1 h-1.5 bg-primary/10 overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${d.score}%` }} />
                      </div>
                      <span className="font-mono text-xs font-bold text-primary w-7 text-right">
                        {d.score}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeUp>

          {/* Archetype + top systems + symptoms */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <FadeUp className="md:col-span-2">
              <Panel label="Primary Optimization Archetype" testid="sample-page-archetype" className="h-full">
                <h2 className="font-display text-xl md:text-2xl font-bold text-primary leading-tight">
                  {SAMPLE_PROFILE.archetype.name}
                </h2>
                <p className="mt-3 text-base text-foreground/75 leading-relaxed">
                  {SAMPLE_PROFILE.archetype.body}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {SAMPLE_PROFILE.topSystems.map((code) => (
                    <span
                      key={code}
                      className="font-mono text-[0.62rem] uppercase tracking-[0.14em] font-bold text-primary bg-primary/8 border border-primary/25 px-3 py-1.5"
                    >
                      {byCode[code].name} · {byCode[code].score}
                    </span>
                  ))}
                </div>
              </Panel>
            </FadeUp>
            <FadeUp delay={0.06}>
              <Panel label="Key Symptoms Reported" testid="sample-page-symptoms" className="h-full">
                <ul className="space-y-2.5">
                  {SAMPLE_PROFILE.symptoms.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-foreground/75 leading-relaxed">
                      <span aria-hidden className="mt-[7px] h-1.5 w-1.5 bg-accent flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </Panel>
            </FadeUp>
          </div>

          {/* Symptom ↔ system relationships */}
          <FadeUp delay={0.08} className="mt-5">
            <Panel label="Potential Symptom ↔ System Relationships" testid="sample-page-relationships">
              <div className="space-y-4">
                {SAMPLE_PROFILE.relationships.map((r) => (
                  <div
                    key={r.symptom}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-baseline border-b border-border pb-4 last:border-0 last:pb-0"
                  >
                    <div className="md:col-span-4 font-display text-sm md:text-base font-semibold text-primary">
                      {r.symptom}
                    </div>
                    <div className="md:col-span-8 flex flex-wrap items-center gap-2">
                      <span aria-hidden className="hidden md:inline font-mono text-accent text-xs">→</span>
                      {r.systems.map((s) => (
                        <span
                          key={s}
                          className="font-mono text-[0.58rem] uppercase tracking-[0.12em] text-foreground/70 border border-border px-2.5 py-1"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </FadeUp>

          {/* Findings grid */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <FadeUp>
              <Panel label="Body-Composition Findings" testid="sample-page-bodycomp" className="h-full">
                <FindingsTable rows={SAMPLE_PROFILE.bodyComp} />
              </Panel>
            </FadeUp>
            <FadeUp delay={0.05}>
              <Panel label="Energy / Recovery Findings" testid="sample-page-energy" className="h-full">
                <FindingsTable rows={SAMPLE_PROFILE.energyRecovery} />
              </Panel>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Panel label="Relevant Laboratory Findings" testid="sample-page-labs" className="h-full">
                <FindingsTable rows={SAMPLE_PROFILE.labs} />
              </Panel>
            </FadeUp>
          </div>

          {/* Priorities */}
          <FadeUp delay={0.08} className="mt-5">
            <Panel label="Optimization Priorities — Ranked" testid="sample-page-priorities">
              <ol className="space-y-3">
                {SAMPLE_PROFILE.priorities.map((p, i) => (
                  <li key={p} className="flex items-start gap-4">
                    <span className="font-mono text-sm font-bold text-accent flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-base text-foreground/80 leading-relaxed">{p}</span>
                  </li>
                ))}
              </ol>
            </Panel>
          </FadeUp>

          <HRule />

          {/* Tier framework */}
          <FadeUp>
            <div className="text-center max-w-3xl mx-auto">
              <div className="iw-overline">The Output</div>
              <h2 className="mt-4 font-display font-bold tracking-tight text-[clamp(1.6rem,3.5vw,2.5rem)] text-primary">
                The Tier 1–4 Optimization Framework
              </h2>
              <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
                Every analysis maps findings to a four-tier intervention landscape. Nothing is
                hidden, locked or sequentially gated — the full picture goes to your clinical team
                for review.
              </p>
            </div>
          </FadeUp>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t, i) => (
              <FadeUp key={t.n} delay={i * 0.06}>
                <div
                  data-testid={`sample-page-tier-${t.n}`}
                  className="h-full border-2 border-primary/15 bg-card p-6"
                >
                  <span className="font-mono text-[0.65rem] font-bold tracking-[0.2em] text-accent">
                    TIER {t.n}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-primary leading-snug">
                    {t.name}
                  </h3>
                  <p className="mt-2.5 text-sm text-foreground/70 leading-relaxed">{t.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* CTA */}
          <FadeUp delay={0.1} className="mt-16 text-center">
            <a
              href={ACTIVATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sample-page-start-analysis"
              className="iw-btn-red inline-flex items-center gap-2 px-9 py-4.5 py-4 text-[0.72rem]"
            >
              Start Your Optimization Analysis <span aria-hidden>→</span>
            </a>
            <PostIntakeSteps testId="sample-analysis-post-intake-steps" className="mt-5 justify-center" />
            <p className="mt-8 max-w-2xl mx-auto font-mono text-[0.62rem] uppercase tracking-[0.16em] leading-relaxed text-foreground/45">
              This is a fictional, de-identified example for education only — not medical advice.
              Interventions involving diagnosis or treatment are always clinician-managed.
            </p>
          </FadeUp>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
